<script setup lang="ts">
import {ref, reactive, inject, nextTick, computed, useTemplateRef} from "vue";
import {useScroll} from "@vueuse/core";
import {useToast} from "primevue/usetoast";
import type dayjs from "dayjs";
import {getAlbieConversation, sendAlbieMessage, clearAlbieConversation} from "@/Services/conversations";
import {PageTitle} from "@/Components/Global";
import {Button, Icon, Textarea, Skeleton} from "@/Components/Library";
import AiConversationMessage from "./AiConversationMessage.vue";
import type {AiChat, AiChatMessage} from "./ai-conversation-types";
import {useConfirm} from "primevue/useconfirm";

const $date = inject("date") as typeof dayjs;
const toast = useToast();
const confirm = useConfirm();

const state = reactive({
    loading: {
        conversation: true,
        sending: false,
    },
});

const chat = ref<AiChat | null>(null);
const messages = ref<Array<AiChatMessage>>([]);
const newMessage = ref("");

const messagesPane = useTemplateRef<HTMLElement>("messagesPane");
const {arrivedState} = useScroll(messagesPane);

const showScrollDownButton = computed(() => !arrivedState.bottom && messages.value.length > 0);

const getChat = async () => {
    try {
        state.loading.conversation = true;
        const {data} = await getAlbieConversation();
        chat.value = data;
        messages.value = data.messages || [];
        scrollToEnd("auto");
    } catch (error) {
        console.log(error);
    } finally {
        state.loading.conversation = false;
    }
};

const scrollToEnd = async () => {
    await nextTick();
    const container = messagesPane.value;
    if (!container) {
        return;
    }

    container.scrollTo({top: container.scrollHeight, behavior: "smooth"});
};

const isSameDate = (message: AiChatMessage, ind: number) => {
    return (
        ind > 0 &&
        $date(message.created_at).format("YYYYMMDD") === $date(messages.value[ind - 1].created_at).format("YYYYMMDD")
    );
};

const sendMessage = async () => {
    if (!newMessage.value.trim() || !chat.value) {
        return;
    }

    const messageBody = newMessage.value;

    try {
        state.loading.sending = true;
        newMessage.value = "";

        const optimisticUserMessage: AiChatMessage = {
            id: `temp-${Date.now()}`,
            ai_chat_id: chat.value.id,
            role: "user",
            text: messageBody,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            deleted_at: null,
        };

        messages.value.push(optimisticUserMessage);
        await nextTick();
        scrollToEnd();

        const {data: aiMessage} = await sendAlbieMessage(chat.value.id, messageBody);
        messages.value.push(aiMessage);

        await nextTick();
        scrollToEnd();
    } catch (error) {
        console.log(error);
        newMessage.value = messageBody;
    } finally {
        state.loading.sending = false;
    }
};

const clearChat = async () => {
    if (!chat.value) {
        return;
    }

    confirm.require({
        header: "Clear Chat History",
        message: "Are you sure you want to clear the chat history?",
        accept: async () => {
            try {
                await clearAlbieConversation(chat.value?.id as string);
                messages.value = [];
                toast.add({
                    severity: "success",
                    summary: "Success",
                    detail: "Chat history cleared",
                    life: 3000,
                });
            } catch (error) {
                console.log(error);
            }
        },
        acceptClass: "bg-red-600 text-white px-2 py-1 rounded-lg hover:brightness-110",
        rejectClass: "px-2 py-1 hover:brightness-110",
    });
};

const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        sendMessage();
    }
};

getChat();
</script>

<template>
    <div class="absolute top-0 z-10 flex h-full w-full grow flex-col overflow-hidden">
        <PageTitle :title="'Chat with Albie'" class="mx-auto mb-2 max-w-5xl bg-white py-3 dark:bg-stone-950">
            <Button
                v-if="messages.length > 0 && !state.loading.conversation"
                icon="prime:trash"
                class="ml-auto text-red-600"
                v-tooltip.left="'Clear Chat History'"
                @click="clearChat"
            />
        </PageTitle>
        <p class="mx-auto mb-2 max-w-5xl px-6 text-xs text-stone-600 dark:text-stone-400">
            Albie is an AI agent. Like all AI agents Albie’s responses may contain errors, hallucinations or incomplete
            reasoning. Do not rely on Albie’s responses for high-stakes decisions.
        </p>

        <div class="flex min-h-0 grow flex-col">
            <div class="relative min-h-0 flex-1">
                <div class="h-full overflow-y-auto px-6 pb-8 md:pr-8" ref="messagesPane">
                    <div class="mx-auto flex w-full max-w-5xl grow flex-col">
                        <template v-if="state.loading.conversation">
                            <div class="flex flex-col gap-4 py-4">
                                <Skeleton height="4rem" />
                                <Skeleton height="4rem" />
                                <Skeleton height="4rem" />
                            </div>
                        </template>

                        <template v-else-if="messages.length">
                            <template v-for="(message, ind) in messages" :key="message.id">
                                <AiConversationMessage
                                    :message="message"
                                    :ind="ind"
                                    :isSameDate="isSameDate(message, ind)"
                                />
                            </template>
                        </template>

                        <div v-else class="flex h-full items-center justify-center py-20">
                            <div class="flex flex-col items-center">
                                <Icon icon="ri:robot-2-fill" class="mb-4 text-6xl text-askr-green-500" />
                                <h3 class="mb-2 text-xl font-semibold">Chat with Albie</h3>
                                <p class="text-stone-600">Ask me anything about using Askr!</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-if="showScrollDownButton && !state.loading.conversation"
                    class="absolute bottom-4 left-1/2 z-50 -translate-x-1/2"
                >
                    <Button
                        class="flex items-center gap-2 rounded-full bg-stone-950/80 px-4 py-2 text-sm text-white shadow-lg backdrop-blur hover:bg-stone-800"
                        :icon="{icon: 'mingcute:down-fill', width: '20'}"
                        @click="scrollToEnd"
                    >
                        Scroll to bottom
                    </Button>
                </div>
            </div>

            <div
                class="z-50 flex w-full flex-none justify-center border-t bg-white px-8 py-3 dark:border-t-stone-800 dark:bg-stone-950"
            >
                <div class="mx-auto flex w-full max-w-5xl items-center gap-3 xl:mx-0">
                    <Textarea
                        v-model="newMessage"
                        placeholder="Ask Albie a question..."
                        :rows="3"
                        autoResize
                        class="grow"
                        @keydown="handleKeydown"
                        :disabled="state.loading.sending"
                    />
                    <Button
                        :disabled="!newMessage.trim() || state.loading.sending"
                        :loading="state.loading.sending"
                        @click="sendMessage"
                        class="flex h-12 w-12 items-center justify-center rounded-full bg-askr-green-500 p-2 text-white hover:bg-askr-green-600"
                        :icon="{icon: 'ic:round-send', width: '24'}"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
